import React from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  Camera,
  CheckSquare,
  Clock,
  FileCheck,
  FileText,
  Flag,
  Hash,
  ListChecks,
  Monitor,
  QrCode,
  ShieldCheck,
  Signature,
  Smartphone,
  Thermometer,
  Zap,
} from "lucide-react";
import SeoManager from "../../components/SEO/SeoManager";

const COMPORTAMIENTOS = [
  { name: "Entrada numerica", desc: "Captura de valores y medidas", icon: <Hash size={18} /> },
  { name: "Check SI/NO", desc: "Validacion simple y obligatoria", icon: <CheckSquare size={18} /> },
  { name: "Cronometro", desc: "Tiempos reales de inicio y fin", icon: <Clock size={18} /> },
  { name: "Marca de tiempo", desc: "Registro exacto de fecha y hora", icon: <Calendar size={18} /> },
  { name: "Texto libre", desc: "Notas y observaciones de operacion", icon: <FileText size={18} /> },
  { name: "Foto obligatoria", desc: "Evidencia visual por actividad", icon: <Camera size={18} /> },
  { name: "Firma digital", desc: "Validacion del operario o supervisor", icon: <Signature size={18} /> },
  { name: "QR o barras", desc: "Lectura de lotes, activos o referencias", icon: <QrCode size={18} /> },
];

const PASOS_PROCESO = [
  { id: "A001", name: "Inicio de inspeccion", behavior: "Solo inicio", icon: <Clock size={28} className="text-primaryBlue" />, dep: "Ninguna", color: "hover:border-l-primaryBlue" },
  { id: "A002", name: "Temperatura del horno", behavior: "Cantidad (C)", icon: <Thermometer size={28} className="text-orange-500" />, dep: "A001", color: "hover:border-l-orange-500" },
  { id: "A003", name: "Evidencia del producto", behavior: "Foto", icon: <Camera size={28} className="text-primaryBlue" />, dep: "A002", color: "hover:border-l-primaryBlue" },
  { id: "A004", name: "Checklist de calidad", behavior: "Check multiple", icon: <ListChecks size={28} className="text-blue-600" />, dep: "A003", color: "hover:border-l-blue-600" },
  { id: "A005", name: "Firma del operario", behavior: "Firma digital", icon: <Signature size={28} className="text-slate-700" />, dep: "A004", color: "hover:border-l-slate-700" },
  { id: "A006", name: "Acta de liberacion", behavior: "Documento PDF", icon: <FileCheck size={28} className="text-green-600" />, dep: "A005", color: "hover:border-l-green-600" },
  { id: "A007", name: "Firma del supervisor", behavior: "Firma digital", icon: <ShieldCheck size={28} className="text-indigo-600" />, dep: "A006", color: "hover:border-l-indigo-600" },
  { id: "A008", name: "Cierre del proceso", behavior: "Solo fin", icon: <Flag size={28} className="text-red-500" />, dep: "A007", color: "hover:border-l-red-500" },
];

const BehaviorCard = ({ icon, name, desc }) => (
  <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-white/10 hover:border-primaryBlue/50 transition-all group">
    <div className="text-primaryBlue group-hover:scale-110 transition-transform">{icon}</div>
    <div>
      <p className="text-[9px] font-bold text-primaryBlue uppercase tracking-widest leading-none mb-1">{name}</p>
      <p className="text-xs font-medium text-slate-300">{desc}</p>
    </div>
  </div>
);

export default function SoftwareManufactura() {
  const canonicalUrl = "https://prodsyncapp.com/software-para-manufactura";
  const title = "Software para Manufactura con Trazabilidad Operativa | PRODSYNC";
  const description =
    "Software para manufactura con control operativo, tiempos reales, evidencias fotograficas, firmas digitales y trazabilidad completa en planta.";
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "PRODSYNC para Manufactura",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, Android",
    url: canonicalUrl,
    description,
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 antialiased">
      <SeoManager
        title={title}
        description={description}
        canonical={canonicalUrl}
        jsonLd={jsonLd}
      />

      <h1 className="sr-only">
        Software para manufactura con control operativo y evidencias en tiempo real
      </h1>

      <section className="py-20 bg-primaryDark text-white relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primaryBlue/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="space-y-6">
              <span className="inline-block px-4 py-1 rounded-full text-xs font-bold bg-primaryBlue/20 text-primaryBlue border border-primaryBlue/30 uppercase tracking-widest">
                Software para manufactura
              </span>

              <p className="text-4xl md:text-6xl font-extrabold leading-tight">
                Controla la operacion de planta con
                <br />
                <span className="text-primaryBlue">trazabilidad real.</span>
              </p>

              <p className="text-slate-400 text-lg leading-relaxed max-w-lg">
                PRODSYNC digitaliza inspecciones, liberaciones, checklists, tiempos,
                evidencias y aprobaciones para que manufactura opere con estandar,
                visibilidad y control.
              </p>

              <div className="grid sm:grid-cols-2 gap-3 pt-4">
                {COMPORTAMIENTOS.map((b, i) => (
                  <BehaviorCard key={i} {...b} />
                ))}
              </div>
            </div>

            <div className="hidden lg:block relative group">
              <div className="absolute -inset-1 bg-primaryBlue/20 rounded-[2rem] blur-xl opacity-50"></div>

              <div className="relative bg-primaryDark border border-slate-700 rounded-[2rem] overflow-hidden shadow-2xl">
                <div className="bg-slate-800/80 px-6 py-4 border-b border-slate-700 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <Monitor size={18} className="text-primaryBlue" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-300">
                      ADMINISTRADOR WEB
                    </span>
                  </div>
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-slate-600"></div>
                    <div className="w-2 h-2 rounded-full bg-slate-600"></div>
                  </div>
                </div>

                <div className="max-h-[450px] overflow-y-auto p-4 space-y-2 custom-scrollbar">
                  {PASOS_PROCESO.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between p-3 bg-slate-800/40 rounded-xl border border-slate-700/50 hover:bg-slate-800 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <span className="text-[10px] font-mono text-primaryBlue font-bold">
                          {p.id}
                        </span>
                        <span className="text-xs text-slate-200 font-medium">
                          {p.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-[9px] bg-slate-900 text-slate-400 px-2 py-1 rounded border border-slate-700 uppercase">
                          {p.behavior}
                        </span>
                        <Zap size={12} className="text-primaryBlue" />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-slate-900/80 p-4 border-t border-slate-700">
                  <div className="flex justify-between items-center">
                    <p className="text-[9px] text-slate-500 italic">
                      Flujo digital para lotes, calidad y liberaciones
                    </p>
                    <span className="text-[9px] font-bold text-green-500 bg-green-500/10 px-2 py-0.5 rounded">
                      ACTIVO
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-6 justify-center lg:justify-start">
            <Link
              to="/"
              state={{ scrollToForm: true }}
              className="inline-flex items-center gap-2 text-primaryBlue font-bold hover:text-primaryBlueHover transition-colors"
            >
              Solicitar demo <span aria-hidden="true">-&gt;</span>
            </Link>
            <Link
              to="/control-operativo-en-campo"
              className="inline-flex items-center gap-2 text-white font-bold hover:text-primaryBlue transition-colors"
            >
              Ver solucion de campo <span aria-hidden="true">-&gt;</span>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white text-slate-900 rounded-t-[3rem] shadow-inner relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-5xl font-black italic tracking-tight">
              Liberacion de lote de produccion
            </h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto font-medium text-balance">
              Modela tus pasos, obliga la captura de evidencia y valida cada aprobacion
              antes de cerrar el proceso.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            {PASOS_PROCESO.map((step) => (
              <div
                key={step.id}
                className={`bg-white p-7 rounded-[2.5rem] border border-slate-200 relative group transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border-l-4 border-l-transparent ${step.color}`}
              >
                <span className="absolute top-6 right-6 text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-50 px-2 py-1 rounded-lg border border-slate-100 shadow-sm">
                  {step.id}
                </span>
                <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center mb-6 shadow-inner group-hover:scale-110 transition-transform">
                  {step.icon}
                </div>
                <h3 className="text-xl font-bold mb-2 tracking-tight group-hover:text-primaryBlue transition-colors leading-snug">
                  {step.name}
                </h3>
                <div className="mt-auto pt-6 border-t border-slate-100 flex flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest leading-none">
                      Comportamiento
                    </p>
                    <div className="flex items-center gap-2 text-[11px] font-bold text-slate-700 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-100 w-fit">
                      <Zap size={14} className="text-primaryBlue" />
                      {step.behavior}
                    </div>
                  </div>
                  <p className="text-[9px] text-slate-400 font-medium italic">
                    Depende de: <span className="text-primaryBlue font-bold">{step.dep}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="p-8 lg:p-12 bg-slate-900 rounded-[3rem] flex flex-col lg:flex-row items-center justify-between gap-10 text-white shadow-2xl overflow-hidden relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primaryBlue/10 rounded-full blur-[100px]" />
            <div className="space-y-4 z-10 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-white/10 text-primaryBlue text-[10px] font-black uppercase tracking-widest">
                <Smartphone size={14} /> Ejecucion en planta
              </div>
              <h3 className="text-3xl md:text-4xl font-black italic tracking-tighter leading-none text-white">
                Del procedimiento <span className="text-primaryBlue">a la ejecucion.</span>
              </h3>
              <p className="text-slate-400 text-lg max-w-xl font-medium leading-snug">
                Tus operarios reciben una interfaz guiada para ejecutar actividades,
                registrar evidencia y asegurar cumplimiento sin papel ni reprocesos.
              </p>
            </div>

            <Link
              to="/"
              state={{ scrollToForm: true }}
              className="px-12 py-5 bg-primaryBlue hover:bg-primaryBlueHover text-white rounded-[1.5rem] font-black text-lg transition-all shadow-xl shadow-primaryBlue/20 whitespace-nowrap z-10 hover:scale-105 active:scale-95 text-center flex items-center justify-center"
            >
              Digitalizar mi proceso
            </Link>
          </div>
        </div>
      </section>

      <footer className="py-16 bg-white border-t border-slate-200 text-center px-6">
        <p className="text-slate-400 font-bold italic text-sm leading-relaxed max-w-2xl mx-auto">
          © 2026 - PRODSYNC. Software para manufactura con trazabilidad operativa.
        </p>
      </footer>
    </div>
  );
}
